import React, { useState, useCallback, useRef, useEffect } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";

import { IoIosSearch } from "react-icons/io";
import { HeaderSearchContainer } from "./HeaderSearchStyled";
import { SearchOverlay } from "../SearchOverlay";
import { SearchResultsResponse } from "../SearchResults";
import BuscasService from "../../../../services/buscas.service";
import { ALL_SEARCH_TYPES } from "../SearchBar";

export interface Props {
    placeholder?: string;
    onItemClick?: (item: any) => void;
    showButton?: boolean;
    size?: "small" | "medium" | "large";
}

const HeaderSearch: React.FC<Props> = ({ placeholder = "Digite aqui para buscar...", onItemClick, showButton = true, size = "medium" }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<SearchResultsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showResults, setShowResults] = useState(false);
    const searchTimeout = useRef<NodeJS.Timeout>();
    const abortControllerRef = useRef<AbortController | null>(null);
    const buscasServiceRef = useRef<BuscasService>(new BuscasService());

    // Auto-search com debounce e cancelamento de requisições
    const performSearch = useCallback(async (query: string, page: number = 1) => {
        if (!query.trim()) {
            setSearchResults(null);
            setShowResults(false);
            return;
        }

        // Cancel previous request if it exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        setLoading(true);
        setShowResults(true);

        try {
            const results = await buscasServiceRef.current.searchComplete({
                q: query,
                tipos: ALL_SEARCH_TYPES,
                ordering: "", // relevância como padrão
                page: 1,
            });

            // Only update state if request wasn't cancelled
            if (!abortControllerRef.current?.signal.aborted) {
                // Ordenar por relevância (search_score) - sempre no header
                let sortedResults = results.results || [];
                if (sortedResults.length > 0) {
                    sortedResults = sortedResults.sort((a: any, b: any) => {
                        const scoreA = a.search_score || 0;
                        const scoreB = b.search_score || 0;
                        return scoreB - scoreA; // Ordem decrescente (maior score primeiro)
                    });
                }

                // Limitar a 3 resultados para o header
                const limitedResults = sortedResults.slice(0, 3);
                const safeResults = {
                    ...results,
                    results: limitedResults,
                    count: results.count || 0,
                    next: results.next || null,
                    previous: results.previous || null,
                    hasMore: (results.count || 0) > 3, // Flag para indicar se há mais resultados
                    originalQuery: query, // Salvar query original para navegação
                };

                setSearchResults(safeResults);
                setCurrentPage(page);
            }
        } catch (error) {
            // Only handle error if request wasn't cancelled
            if (!abortControllerRef.current?.signal.aborted) {
                console.error("Erro na busca:", error);
                setSearchResults({
                    error: true,
                    message: "Erro ao realizar busca",
                    count: 0,
                    next: null,
                    previous: null,
                    results: [],
                });
            }
        } finally {
            if (!abortControllerRef.current?.signal.aborted) {
                setLoading(false);
            }
        }
    }, []);

    // Debounced search
    const handleSearchChange = useCallback(
        (query: string) => {
            setSearchQuery(query);

            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }

            searchTimeout.current = setTimeout(() => {
                if (query.trim()) {
                    performSearch(query);
                } else {
                    setSearchResults(null);
                    setShowResults(false);
                }
            }, 500);
        },
        [performSearch]
    );

    const handleSearchSubmit = () => {
        if (searchQuery.trim()) {
            performSearch(searchQuery);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSearchSubmit();
        }
    };

    const handleCloseResults = () => {
        setShowResults(false);
    };

    const handlePageChange = useCallback(
        async (page: number) => {
            if (searchQuery.trim()) {
                await performSearch(searchQuery, page);
            }
        },
        [searchQuery, performSearch]
    );

    const handleItemClickInternal = useCallback(
        (item: any) => {
            setShowResults(false);
            onItemClick?.(item);
        },
        [onItemClick]
    );

    useEffect(() => {
        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return (
        <>
            <HeaderSearchContainer size={size} data-size={size}>
                <Box display="flex" alignItems="center" gap={1} width="100%">
                    <TextField
                        fullWidth
                        size={size === "large" ? "medium" : "small"}
                        variant="outlined"
                        placeholder={placeholder}
                        value={searchQuery}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        onKeyPress={handleKeyPress}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IoIosSearch size={size === "large" ? 20 : 18} /> {/* Reduzido proporcionalmente */}
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "white",
                                fontSize: size === "large" ? "1rem" : size === "medium" ? "0.95rem" : "0.9rem" /* Reduzido um pouco */,
                                height: size === "large" ? "42px" : size === "medium" ? "38px" : "34px" /* Altura específica */,
                                "& fieldset": {
                                    borderColor: "#e0e0e0",
                                    borderWidth: size === "large" ? "2px" : "1px",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#013a7b",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "#013a7b",
                                    borderWidth: size === "large" ? "2px" : "1px",
                                },
                            },
                        }}
                    />

                    {showButton && (
                        <Button
                            variant="contained"
                            onClick={handleSearchSubmit}
                            disabled={loading || !searchQuery.trim()}
                            size={size}
                            sx={{
                                minWidth: size === "large" ? "120px" : "80px",
                                height: size === "large" ? "48px" : size === "medium" ? "40px" : "36px",
                                fontSize: size === "large" ? "1rem" : "0.875rem",
                                fontWeight: 600,
                            }}>
                            {loading ? "..." : "Buscar"}
                        </Button>
                    )}
                </Box>
            </HeaderSearchContainer>

            {/* Search Overlay for Results */}
            <SearchOverlay
                isOpen={
                    showResults &&
                    (loading || (searchResults && searchResults.results && searchResults.results.length > 0) || Boolean(searchResults?.error))
                }
                searchQuery={searchQuery}
                searchResults={searchResults}
                loading={loading}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onClose={handleCloseResults}
                onItemClick={handleItemClickInternal}
                showViewMore={true}
            />
        </>
    );
};

export { HeaderSearch };
